import type { MouseEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { EntitlementSlugEnum } from '@electricjs/core_entity-client';
import { Card, Flex, Button, Text, Tooltip, spaces } from '@electricjs/arc';

import { StorefrontAsset } from '@/types/assets';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import { Store } from '@/types/storefront';
import { type Product } from '@/types/products';
import { ELECTRIC_WAREHOUSE_SITE_NAME } from '@/constants/assets';
import formatCurrency from '@/helpers/formatCurrency';
import { getCountryName } from '@/helpers/getCountryName';
import { calculatePreTaxMinMonthlyInstallment } from '../utils';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { useStorefront } from '../StorefrontProvider';

import { ProductDescription } from './Catalog/ProductDescription';
import AssetDescription from './Asset/AssetDescription';

type CatalogCard = {
  store: Store.Catalog;
  item: Product;
};

type AssetCard = {
  store: Store.Asset;
  item: StorefrontAsset;
};

type ItemCardProps = (CatalogCard | AssetCard) & {
  idPrefix: string;
  selected: boolean;
  onAddItem: () => void;
  onRemoveItem: () => void;
  onClick?: () => void;
  isCondensed?: boolean;
  groupNames?: string[];
};

export const ItemCard = ({
  idPrefix,
  store,
  item,
  selected,
  onAddItem,
  onRemoveItem,
  onClick,
  isCondensed,
  groupNames,
}: ItemCardProps) => {
  const { t } = useTranslation('storefront');
  const [queryParams, writeQueryParams] = useSearchParams();
  const { techveraOutboundLocked, storefrontPayOverTime } = useFlags();

  const hasInstallmentsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Installments
  );
  const showFinancing = storefrontPayOverTime && hasInstallmentsEntitlement;

  const { storefrontCheckoutForm } = useStorefront();
  const isEmployeeSelected = Boolean(
    storefrontCheckoutForm?.getValues('employee')?.value
  );
  const shoppingCountryCode =
    storefrontCheckoutForm?.getValues('shoppingCountry')?.value;

  // Just for typescript, because this component will never be rendered in a use-case that the shopping country doesn't exist or is "other"
  if (!shoppingCountryCode || shoppingCountryCode === 'other') {
    throw new Error();
  }

  const productPrice =
    store === Store.Catalog ? (item as Product).price : undefined;
  const isUnpriced = store === Store.Catalog && productPrice === undefined;
  const isAssetUnshippable =
    store === Store.Asset && (item as StorefrontAsset).isShippable === false;
  const isTechveraOutboundLocked =
    store === Store.Asset &&
    techveraOutboundLocked === true &&
    (item as StorefrontAsset).site?.name === ELECTRIC_WAREHOUSE_SITE_NAME;
  const shouldBlockAddingToCart =
    isTechveraOutboundLocked ||
    !isEmployeeSelected ||
    isUnpriced ||
    isAssetUnshippable;
  const blockedTooltip = isTechveraOutboundLocked
    ? t('itemCard.outboundLockedTooltip')
    : isAssetUnshippable
      ? t('itemCard.notAvailableInCountry', {
          country: getCountryName(shoppingCountryCode),
        })
      : isUnpriced
        ? t('itemCard.unpricedTooltip')
        : t('itemCard.addToCartTooltip');
  const addToCartButtonText = isAssetUnshippable
    ? t('itemCard.notAvailableInCountry', {
        country: getCountryName(shoppingCountryCode),
      })
    : t('common.addToCart');

  const openItemDetailsModal = () => {
    // just need to set a query param, because the application will automatically open the modal when the param (productId or AssetId) exists in the URL
    if (store === Store.Catalog) {
      queryParams.set(SelectionParamKeys.ProductId, item.id);
    } else if (store === Store.Asset) {
      queryParams.set(SelectionParamKeys.AssetId, item.id);
    }

    writeQueryParams(queryParams, {
      // Each change in the query params will replace the current history state, meaning the "back button" will go back to the previous page.
      replace: true,
    });
  };

  return (
    <Card
      position="relative"
      id={`${idPrefix}-item-card-${item.id}`}
      width="100%"
      height="100%" // keeping all cards of a row with the same height, independent of their content
      cardPadding={isCondensed ? spaces.space_m : spaces.space_l}
      titleBodyDistance="0" // as the title is not being used
      noBorder={isCondensed}
      onClick={onClick ?? openItemDetailsModal} // if onClick is not passed, call the default action ("openItemDetailsModal")
      footer={
        // Card's footer (price, add/remove button), always tied to the bottom
        <>
          {store === Store.Catalog && (
            <Flex vertical mt={isCondensed ? spaces.space_m : spaces.space_s}>
              <Text variant={isCondensed ? 'label-large' : 'heading-3'}>
                {isUnpriced
                  ? t('itemCard.priceUnavailable')
                  : formatCurrency(productPrice, item.currencyCode)}
              </Text>
              {showFinancing && productPrice !== undefined && (
                <Text variant={isCondensed ? 'legal' : 'body-2'} intent="brand">
                  {t('itemCard.orAsLowAs', {
                    amount: formatCurrency(
                      calculatePreTaxMinMonthlyInstallment(productPrice),
                      item.currencyCode
                    ),
                  })}
                </Text>
              )}
            </Flex>
          )}
          <Flex mt={isCondensed ? spaces.space_s : spaces.space_m}>
            {!selected ? (
              <Tooltip
                content={blockedTooltip}
                disabled={!shouldBlockAddingToCart}
                placement="bottom"
                wrapperWidth="100%">
                <Button
                  id={`${idPrefix}-add-to-cart-button-${item.id}`}
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    // To avoid opening the modal
                    event.stopPropagation();
                    onAddItem();
                  }}
                  buttonSize={isCondensed ? 'small' : 'large'}
                  paddingY={isCondensed ? spaces.space_2xs : undefined}
                  fluid
                  testId="add-button"
                  disabled={shouldBlockAddingToCart}>
                  {addToCartButtonText}
                </Button>
              </Tooltip>
            ) : (
              <Button
                id={`${idPrefix}-remove-from-cart-button-${item.id}`}
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  // To avoid opening the modal
                  event.stopPropagation();
                  onRemoveItem();
                }}
                buttonSize={isCondensed ? 'small' : 'large'}
                paddingY={isCondensed ? spaces.space_2xs : undefined}
                fluid
                testId="remove-button"
                variant="outline"
                iconAfter="trashcan">
                {t('common.remove')}
              </Button>
            )}
          </Flex>
        </>
      }>
      {/* Card's body */}
      {store === Store.Catalog && (
        <ProductDescription
          image={item.image}
          name={item.name}
          dynamicProperties={item.dynamicProperties}
          groupNames={groupNames}
          isCondensed={isCondensed}
        />
      )}
      {store === Store.Asset && (
        <AssetDescription
          customAssetId={item.customAssetId}
          name={item.name}
          brand={item.brand}
          condition={item.condition}
          model={item.model}
          serialNumber={item.serialNumber}
          site={item.site}
          isShippable={item.isShippable}
          selfShipRequired={item.selfShipRequired}
        />
      )}
    </Card>
  );
};
